import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, ArrowRight, LogOut } from "lucide-react";
import { useBusiness } from "@/context/BusinessContext";
import { useAuth } from "@/context/AuthContext";
import { Spinner } from "@/components/ui";

const STEPS = 2; // currently actionable setup steps: profile + instructions

function progressOf(b) {
  const o = b.onboarding || {};
  let done = 0;
  if (o.profileComplete) done += 1;
  if (o.playbookPublished) done += 1;
  return done;
}

function WorkspaceCard({ b, onOpen, onEdit, delay }) {
  const done = progressOf(b);
  const pct = Math.round((done / STEPS) * 100);
  const ready = done >= STEPS;
  const tone = ready ? "var(--accent)" : "var(--accent-2)";
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }}
      data-testid={`workspace-card-${b.id}`}
      className="p-6 flex flex-col gap-4"
      style={{ background: "var(--s1)", border: "1px solid var(--border)" }}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center justify-center mono text-lg font-semibold"
          style={{ width: 44, height: 44, background: "var(--s3)", border: "1px solid var(--border-strong)", color: tone }}>
          {b.name?.[0]?.toUpperCase()}
        </div>
        <span className="mono text-[10px] uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{b.role}</span>
      </div>
      <div>
        <div className="font-medium text-lg" style={{ color: "var(--text)" }}>{b.name}</div>
        <div className="mono text-xs mt-1" style={{ color: tone }}>{ready ? "Ready to go" : `Setup ${done} of ${STEPS}`}</div>
      </div>
      <div style={{ height: 4, background: "var(--s3)" }}>
        <div style={{ height: "100%", width: `${pct}%`, background: tone, transition: "width .3s" }} />
      </div>
      <div className="flex gap-2 mt-1">
        <button onClick={() => onOpen(b)} data-testid={`workspace-open-${b.id}`} className="btn btn-sm flex-1" disabled={!ready} title={ready ? "" : "Finish setup first"}>
          Open <ArrowRight size={13} />
        </button>
        <button onClick={() => onEdit(b)} data-testid={`workspace-edit-${b.id}`} className="btn btn-accent2 btn-sm flex-1">
          Edit setup
        </button>
      </div>
    </motion.div>
  );
}

function HubHeader() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  return (
    <header className="flex items-center justify-between px-6 md:px-12 h-16" style={{ borderBottom: "1px solid var(--border)", background: "var(--s1)" }}>
      <Link to="/" className="flex items-center gap-2.5" data-testid="hub-logo" title="Home">
        <div style={{ width: 20, height: 20, background: "var(--accent)" }} />
        <span className="mono font-semibold tracking-tight">Wappy</span>
      </Link>
      <div className="flex items-center gap-4">
        <span className="mono text-xs hidden sm:block" style={{ color: "var(--text-muted)" }}>{user?.email}</span>
        <button className="btn btn-sm" data-testid="hub-logout" onClick={async () => { await logout(); nav("/"); }}>
          <LogOut size={14} /> Sign out
        </button>
      </div>
    </header>
  );
}

export default function WorkspacesHub() {
  const { businesses, loading, setActive } = useBusiness();
  const nav = useNavigate();

  const open = (b) => { setActive(b.id); nav("/app/dashboard"); };
  const edit = (b) => { setActive(b.id); nav("/onboarding/business"); };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--canvas)" }}>
      <HubHeader />

      {loading ? (
        <div className="flex-1 flex justify-center items-center"><Spinner size={24} /></div>
      ) : !businesses.length ? (
        <div className="flex-1 flex items-center justify-center px-6">
          <div className="text-center" style={{ maxWidth: 460 }}>
            <div className="mono text-[11px] uppercase tracking-widest mb-3" style={{ color: "var(--accent-2)" }}>Welcome to Wappy</div>
            <h1 className="tracking-tight" style={{ fontWeight: 300, fontSize: "clamp(1.8rem,4vw,2.6rem)", color: "var(--text)" }}>
              Let's set up your first workspace.
            </h1>
            <p className="mt-4 text-base" style={{ color: "var(--text-2)", fontWeight: 300 }}>
              A workspace is your business on Wappy. It takes a couple of minutes, and we'll guide you the whole way.
            </p>
            <button className="btn btn-accent2 mt-8 mx-auto" style={{ height: 46, paddingInline: 24 }} data-testid="hub-create-first"
              onClick={() => nav("/onboarding/business")}>
              <Plus size={16} /> Create your workspace
            </button>
          </div>
        </div>
      ) : (
        <div className="flex-1 px-6 md:px-12 py-12">
          <div className="max-w-5xl mx-auto">
            <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))" }}>
              {businesses.map((b, i) => <WorkspaceCard key={b.id} b={b} onOpen={open} onEdit={edit} delay={i * 0.05} />)}
              <motion.button
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: businesses.length * 0.05 }}
                onClick={() => nav("/onboarding/business")} data-testid="hub-create-new"
                className="flex flex-col items-center justify-center gap-3 p-6 transition-colors"
                style={{ border: "1px dashed var(--border-strong)", color: "var(--text-2)", minHeight: 200 }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "var(--accent-2)")}
                onMouseLeave={(e) => (e.currentTarget.style.color = "var(--text-2)")}>
                <Plus size={22} />
                <span className="text-sm">New workspace</span>
              </motion.button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
